import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthShell } from '@/components/AuthShell';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { ApiError } from '@/lib/api';
import { authApi } from '@/features/auth/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await authApi.forgotPassword(email);
      setDevLink(res.devLink ?? null);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We'll email you a secure link to set a new password."
      footer={
        <Link to="/login" className="font-semibold text-brand-soft hover:text-brand">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div className="space-y-3">
          <Alert tone="success">
            If an account exists for that email, a reset link is on its way.
          </Alert>
          {devLink && (
            <Alert tone="info">
              Email isn't configured yet —{' '}
              <Link to={devLink.replace(/^https?:\/\/[^/]+/, '')} className="font-semibold text-brand-soft underline">
                use this reset link
              </Link>
              .
            </Alert>
          )}
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {error && <Alert tone="error">{error}</Alert>}
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
